import React from "react";
import { Link } from "react-router-dom";
import { Camera, CameraOff, History, AlertTriangle, Sparkles } from "lucide-react";

const TYPE_META = {
  initial_evaluation: { color: "bg-sage-600", short: "Eval", label: "Initial Evaluation" },
  follow_up: { color: "bg-paper-300", short: "FU", label: "Follow-Up" },
  progress_reassessment: { color: "bg-terracotta-500", short: "Re-x", label: "Progress Reassessment" },
  maintenance: { color: "bg-paper-200", short: "Maint", label: "Maintenance" },
  discharge: { color: "bg-ink-900", short: "DC", label: "Discharge" },
};

/**
 * Horizontal visual timeline of sessions for a client. Pulled from
 * GET /api/clients/{id}/photo-timeline.
 *
 * Each marker shows:
 *  - colored dot by session_type
 *  - camera icon if photos
 *  - "major change" badge if progress_since_last had worse/new_symptoms
 *  - hover/tap reveals date + type + pain delta
 */
export default function PhotoTimeline({ items = [], clientId }) {
  if (!items.length) {
    return (
      <div className="rounded-3xl border border-paper-200 bg-paper-50 p-5 text-sm text-ink-500" data-testid="photo-timeline-empty">
        No sessions yet — once the first session is completed, the progress timeline will appear here.
      </div>
    );
  }
  const photoCount = items.filter((i) => i.has_photos).length;
  const lastPhoto = [...items].reverse().find((i) => i.has_photos);

  return (
    <div className="rounded-3xl border border-paper-200 bg-white p-5 sm:p-6" data-testid="photo-timeline">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="label-overline mb-1">Progress timeline</div>
          <div className="text-sm text-ink-700">
            {items.length} visit{items.length === 1 ? "" : "s"} · {photoCount} with photos
            {lastPhoto && (
              <span className="text-ink-500"> · last photos {new Date(lastPhoto.date).toLocaleDateString()}</span>
            )}
          </div>
        </div>
        <Legend />
      </div>
      <div className="relative pb-4" data-testid="timeline-track">
        {/* Track line */}
        <div className="absolute left-0 right-0 top-5 h-px bg-paper-200" />
        {/* Markers */}
        <div className="flex items-start gap-2 overflow-x-auto pb-2 -mb-2">
          {items.map((it) => {
            const meta = TYPE_META[it.session_type] || TYPE_META.follow_up;
            const Icon = it.has_photos ? Camera : CameraOff;
            const painDelta = (it.pain_before != null && it.pain_after != null)
              ? it.pain_before - it.pain_after
              : null;
            return (
              <Link
                key={it.session_id}
                to={`/clients/${clientId}/sessions/${it.session_id}`}
                data-testid={`timeline-marker-${it.session_id}`}
                className="group shrink-0 w-[88px] flex flex-col items-center text-center relative"
                title={`Visit #${it.visit_number} · ${meta.label}`}
              >
                <div className={`relative z-10 w-10 h-10 rounded-full ${meta.color} flex items-center justify-center text-white text-[10px] font-semibold ring-4 ring-white`}>
                  {meta.short}
                  {it.major_change && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-terracotta-500 text-white flex items-center justify-center" data-testid={`timeline-major-${it.session_id}`}>
                      <AlertTriangle className="w-2.5 h-2.5" />
                    </span>
                  )}
                </div>
                <Icon className={`mt-1 w-3.5 h-3.5 ${it.has_photos ? "text-sage-600" : "text-ink-400"}`} />
                <div className="mt-1 text-[10px] text-ink-700">#{it.visit_number}</div>
                <div className="text-[10px] text-ink-500">{new Date(it.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                {painDelta != null && (
                  <div className={`text-[10px] mt-0.5 ${painDelta > 0 ? "text-sage-700" : painDelta < 0 ? "text-terracotta-600" : "text-ink-500"}`}>
                    {painDelta > 0 ? `↓${painDelta}` : painDelta < 0 ? `↑${Math.abs(painDelta)}` : "—"}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-ink-500" data-testid="timeline-legend">
      <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-sage-600" />Eval</span>
      <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-terracotta-500" />Reassess</span>
      <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-paper-300" />Follow-up</span>
      <span className="inline-flex items-center gap-1"><Camera className="w-3 h-3 text-sage-600" />Has photos</span>
    </div>
  );
}
